import {ConceptPanel} from '../components/ConceptPanel';
import {Skeleton} from '../components/Elapsed';
import {Close} from '../components/Icons';
import {PipelineStepper} from '../components/PipelineStepper';
import {ActivityLog, RenderPanel} from '../components/Rail';
import {SceneCard} from '../components/SceneCard';
import {SceneStrip} from '../components/SceneStrip';
import {JOB_LABEL} from '../format';
import {useStudio} from '../store';
import type {JobKind} from '../types';

/** Shown while the queue is still writing scenes, so the page is never just empty. */
function PendingScene({title}: {title: string}) {
  return (
    <div className="panel" style={{borderStyle: 'dashed', padding: '18px 20px'}}>
      <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16}}>
        <span className="spin" style={{width: 16, height: 16}} />
        <span style={{font: '700 14px/1 var(--sans)', color: 'var(--state-work)'}}>{title}…</span>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 268px', gap: 20}}>
        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
          <Skeleton />
          <Skeleton width="88%" />
          <Skeleton width="64%" />
          <Skeleton width="100%" height={44} />
        </div>
        <Skeleton width="100%" height={140} />
      </div>
    </div>
  );
}

function JobToast() {
  const s = useStudio();
  if (!s.toast) return null;
  const currentKind = s.toast.kind;

  return (
    <div className="toast">
      <div className="mark">
        <span className="spin" style={{width: 14, height: 14, borderWidth: 2}} />
      </div>
      <div style={{flex: 1, minWidth: 0}}>
        <b>{JOB_LABEL[currentKind as JobKind] ?? 'جاري المعالجة'}</b>
        <div className="bar">
          <div style={{width: `${s.toast.percent}%`}} />
        </div>
        <p>لا تغلق الصفحة</p>
      </div>
      <button type="button" className="icon-round" title="إخفاء" onClick={s.dismissToast}>
        <Close />
      </button>
    </div>
  );
}

export function WorkspaceScreen() {
  const s = useStudio();
  const project = s.project!;
  const scenes = s.scenes();
  const pipelineError = s.errorFor('pipeline');

  return (
    <main>
      <SceneStrip scenes={scenes} />

      <div className="ws">
        <div className="ws-main">
          <PipelineStepper />

          {s.rateLimited && (
            <div className="notice">
              <div className="mark">!</div>
              <div>
                <b>وصلت إلى حدّ التوليد لهذه الساعة</b>
                <p>
                  يمكنك الاستمرار في التحرير والإنتاج الآن، وسيُعاد فتح التوليد تلقائيًا بعد
                  انقضاء الساعة.
                </p>
              </div>
            </div>
          )}

          {pipelineError && (
            <div className="notice bad">
              <div className="mark">!</div>
              <div>
                <b>تعطّلت خطوة في خط الإنتاج</b>
                <p>{pipelineError}</p>
              </div>
            </div>
          )}

          <ConceptPanel concept={project.document.concept} />

          {scenes.map((scene) => (
            <SceneCard key={scene.id} scene={scene} aspect={project.aspect_ratio} />
          ))}

          {s.toast && scenes.length === 0 && (
            <PendingScene title={JOB_LABEL[s.toast.kind as JobKind] ?? 'جاري المعالجة'} />
          )}
        </div>

        <div className="rail">
          <RenderPanel scenes={scenes} />
          <ActivityLog messages={project.document.messages} />
        </div>
      </div>

      <JobToast />
    </main>
  );
}
